/**
 * Produkt-Rad (Startseite): drehbare Produktreihe.
 *
 * Ohne dieses Skript bleibt die Reihe eine waagerecht wischbare Liste. Mit Skript
 * steht die mittlere Karte gross vorn, die Seiten kippen weg und werden kleiner.
 * Bedienung: Pfeile, Klick auf eine Seitenkarte, Ziehen oder Wischen, Pfeiltasten.
 * Die Reihe laeuft im Kreis.
 */
class FaynProductWheel extends HTMLElement {
  /** @type {HTMLElement[]} */
  #items = [];
  #index = 0;
  /** @type {number | null} */
  #startX = null;
  #moved = false;
  /** @type {HTMLElement[]} */
  #dots = [];
  #onResize = () => this.#layout();
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    const stage = this.querySelector('[data-fayn-wheel-stage]');
    if (!(stage instanceof HTMLElement)) return;

    this.#items = Array.from(stage.querySelectorAll('[data-fayn-wheel-item]')).filter(
      (item) => item instanceof HTMLElement
    );
    if (this.#items.length < 2) return;

    this.#index = Math.max(0, Math.floor(this.#items.length / 2) - 1);

    const dots = this.querySelector('[data-fayn-wheel-dots]');
    if (dots) {
      dots.replaceChildren();
      this.#dots = this.#items.map(() => {
        const dot = document.createElement('span');
        dot.className = 'fayn-wheel__dot';
        dots.appendChild(dot);
        return dot;
      });
    }

    this.classList.add('is-wheel');

    // Die Pfeile liegen links und rechts ueber der Reihe, dadurch entfaellt die Zeile darunter.
    const controls = this.querySelector('.fayn-wheel__controls');
    if (controls) stage.appendChild(controls);

    this.querySelector('[data-fayn-wheel-prev]')?.addEventListener('click', () => this.#go(-1));
    this.querySelector('[data-fayn-wheel-next]')?.addEventListener('click', () => this.#go(1));

    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#go(1);
      }
    });

    stage.addEventListener('pointerdown', (event) => {
      this.#startX = event.clientX;
      this.#moved = false;
    });
    stage.addEventListener('pointermove', (event) => {
      if (this.#startX === null) return;
      if (Math.abs(event.clientX - this.#startX) > 46) {
        this.#go(event.clientX < this.#startX ? 1 : -1);
        this.#startX = event.clientX;
        this.#moved = true;
      }
    });
    const end = () => {
      this.#startX = null;
      setTimeout(() => {
        this.#moved = false;
      }, 60);
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointerleave', end);

    // Seitenkarten drehen zuerst nach vorn, erst die mittlere Karte folgt dem Link.
    this.#items.forEach((item, position) => {
      item.addEventListener('click', (event) => {
        if (this.#moved) {
          event.preventDefault();
          return;
        }
        if (position !== this.#index) {
          event.preventDefault();
          this.#index = position;
          this.#layout();
        }
      });
    });

    // Das Rad darf sich nie seitlich verschieben (sonst wandert der rechte Pfeil beim Klicken).
    stage.addEventListener('scroll', () => {
      if (this.classList.contains('is-wheel') && stage.scrollLeft !== 0) stage.scrollLeft = 0;
    });

    window.addEventListener('resize', this.#onResize);

    // Die Breite steht beim ersten Aufbau oft noch nicht fest. Jede Groessenaenderung misst neu.
    if (typeof ResizeObserver !== 'undefined') {
      this.#observer = new ResizeObserver(() => this.#layout());
      this.#observer.observe(stage);
    }

    this.#layout();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {number} direction */
  #go(direction) {
    const count = this.#items.length;
    this.#index = (this.#index + direction + count) % count;
    this.#layout();
  }

  #layout() {
    const stage = this.querySelector('[data-fayn-wheel-stage]');
    if (!(stage instanceof HTMLElement)) return;

    const count = this.#items.length;
    // Auf schmalen Bildschirmen so schmal, dass die Nachbarkarten komplett ins Bild passen.
    let width = Math.max(100, Math.min(168, stage.clientWidth * 0.34));
    // Neben festen Kacheln: die mittlere Karte ist genauso gross wie die Kacheln
    const promoPic = this.closest('.fayn-wheel')?.querySelector('.fayn-wheel__promo .fayn-wheel__pic');
    if (promoPic) {
      const promoBox = promoPic.getBoundingClientRect();
      if (promoBox.width > 0 && this.getBoundingClientRect().left >= promoBox.right) width = promoBox.width;
    }
    stage.style.setProperty('--fayn-wheel-width', `${width}px`);
    // Etwas mehr als eine Kartenbreite: Name und Preis der Nachbarn bleiben lesbar.
    const step = width * 1.06;

    this.#items.forEach((item, position) => {
      let distance = position - this.#index;
      const half = count / 2;
      if (distance > half) distance -= count;
      if (distance < -half) distance += count;
      const steps = Math.abs(distance);

      item.style.transform = `translateX(${distance * step}px) translateZ(${-steps * 50}px) rotateY(${-distance * 24}deg) scale(${1 - 0.1 * steps})`;
      item.style.opacity = String(steps > 2 ? 0 : 1 - 0.3 * steps);
      item.style.zIndex = String(10 - steps);
      item.style.pointerEvents = steps > 2 ? 'none' : 'auto';
      item.classList.toggle('is-center', steps === 0);
      item.classList.toggle('is-near', steps === 1);
      item.toggleAttribute('inert', steps > 2);
      item.setAttribute('aria-hidden', steps > 0 ? 'true' : 'false');
    });

    this.#dots.forEach((dot, position) => dot.classList.toggle('is-active', position === this.#index));
  }
}

if (!customElements.get('fayn-product-wheel')) {
  customElements.define('fayn-product-wheel', FaynProductWheel);
}
